import React from "react";
import { Calendar, Clock, Video, Check, ArrowRight } from "lucide-react";

const includes = [
  "A clear understanding of your investment goals",
  "Honest review of your current strategy & portfolio",
  "Tailored options across BRRR, Flips and Buy-to-Let",
  "Discussion of an action plan — even if we never work together",
];

const meta = [
  { Icon: Clock, label: "15 Minutes" },
  { Icon: Video, label: "Video or Phone" },
  { Icon: Calendar, label: "Complimentary" },
];

export function BookSession() {
  return (
    <section
      id="book-session"
      data-testid="book-session-section"
      className="bg-[var(--ivory)] py-24 lg:py-32"
    >
      <div className="max-w-[1180px] mx-auto px-6 lg:px-10">
        <div
          className="reveal relative bg-[var(--navy)] text-[var(--ivory)] grain-overlay overflow-hidden"
          style={{ boxShadow: "0 30px 80px -40px rgba(14,23,48,0.6)" }}
        >
          <span className="absolute top-0 left-0 right-0 h-[2px] bg-[var(--gold)]" />
          <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-10 lg:gap-16 items-stretch p-8 sm:p-12 lg:p-16">
            {/* Left copy */}
            <div className="flex flex-col">
              <div className="flex items-center gap-3 mb-6">
                <span className="h-px w-10 bg-[var(--gold)]" />
                <span className="text-[0.7rem] tracking-[0.32em] uppercase text-[var(--gold)]">
                  Free Discovery Call
                </span>
              </div>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-[3.25rem] leading-[1.05]">
                15 Minutes.
                <br />
                <span className="italic text-[var(--gold)]">A clear plan.</span>
              </h2>
              <p
                className="mt-7 text-[1.02rem] leading-[1.75] max-w-xl"
                style={{ color: "rgba(245,240,232,0.82)" }}
              >
                Book a complimentary 15-minute discovery call with our team. We'll listen,
                understand your goals, and formulate the options most suited to
                your strategy and budget — with no pressure and no obligation.
              </p>

              <ul className="mt-8 space-y-3 max-w-lg">
                {includes.map((item, i) => (
                  <li
                    key={item}
                    data-testid={`session-include-${i}`}
                    className="flex items-start gap-3"
                  >
                    <span className="mt-[3px] w-5 h-5 rounded-full border border-[var(--gold)]/70 flex items-center justify-center shrink-0">
                      <Check size={11} className="text-[var(--gold)]" strokeWidth={2.4} />
                    </span>
                    <span
                      className="text-[0.98rem] leading-[1.6]"
                      style={{ color: "rgba(245,240,232,0.85)" }}
                    >
                      {item}
                    </span>
                  </li>
                ))}
              </ul>

              <div className="mt-10 flex flex-wrap items-center gap-5">
                {/*
                  Booking link → Take Flight (GHL) booking widget.
                */}
                <a
                  href="https://api.leadconnectorhq.com/widget/booking/5bERhpD0IznkYqpcQ73T"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="session-cta"
                  className="btn-gold"
                >
                  Book My Discovery Call
                  <ArrowRight size={18} />
                </a>
                <a
                  href="#guide"
                  className="text-[0.8rem] tracking-[0.18em] uppercase text-[var(--ivory)]/80 hover:text-[var(--gold)] transition-colors"
                >
                  Or download the guide →
                </a>
              </div>

              <p className="mt-6 text-[0.78rem] italic text-[var(--ivory)]/55 max-w-sm">
                No sales pitch. Just an honest conversation about where you are
                and where you want to be.
              </p>
            </div>

            {/* Right: what to expect card */}
            <div className="relative flex">
              <div className="w-full bg-[var(--navy-2)] border border-[rgba(201,168,76,0.28)] p-7 sm:p-9 flex flex-col">
                <span className="absolute top-7 right-7 sm:top-9 sm:right-9 text-[0.65rem] tracking-[0.28em] uppercase text-[var(--gold)]/80">
                  Agenda
                </span>
                <p className="font-display text-[1.5rem] leading-tight pr-20">
                  What we'll cover
                </p>
                <ol className="mt-7 space-y-5 flex-1">
                  {[
                    {
                      n: "01",
                      t: "Your goals",
                      d: "Income, growth, exit timeline — what success looks like for you.",
                    },
                    {
                      n: "02",
                      t: "Where you are today",
                      d: "Existing portfolio, capital available, comfort with risk.",
                    },
                    {
                      n: "03",
                      t: "The right strategy",
                      d: "BRRR, Flips or Buy-to-Let — and where to deploy capital first.",
                    },
                    {
                      n: "04",
                      t: "Your next move",
                      d: "A clear discussion of your next steps — practical, honest, and no obligation.",
                    },
                  ].map((s) => (
                    <li key={s.n} className="flex items-start gap-4">
                      <span className="font-display text-[1.15rem] text-[var(--gold)] leading-none mt-[2px] w-7 shrink-0">
                        {s.n}
                      </span>
                      <div>
                        <p
                          className="text-[0.98rem] font-medium"
                          style={{ color: "#F5F0E8" }}
                        >
                          {s.t}
                        </p>
                        <p className="text-[0.88rem] mt-1 text-[var(--ivory)]/65 leading-relaxed">
                          {s.d}
                        </p>
                      </div>
                    </li>
                  ))}
                </ol>
                <div className="mt-7 pt-6 border-t border-[rgba(201,168,76,0.2)] flex items-center justify-between gap-3 flex-wrap">
                  {meta.map(({ Icon, label }) => (
                    <div key={label} className="flex items-center gap-2">
                      <Icon size={14} className="text-[var(--gold)]" />
                      <span className="text-[0.72rem] tracking-[0.18em] uppercase text-[var(--ivory)]/75">
                        {label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
